import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { SelectOption } from '../Select';
import { User } from '@app/models';
import { getSpecializations } from '@app/api';
import { cn } from 'lib/utils';

interface Props {
  value?: string;
  onChange: (value: string) => void;
  index?: number;
  item?: ISelectOption[];
  onSelectedLists?: (item: any[]) => void;
  className?: string;
}

type Specialization<T> = T extends { specialization: infer S }
  ? S extends string
    ? S
    : never
  : never;

type UserSpecialization = Specialization<User>;

interface ISelectOption extends SelectOption<UserSpecialization> {}

const defaultOption = {
  label: 'Все' as const,
  value: 'all' as const,
};

function SpecializationSelect(props: Props) {
  const { onChange, value, className } = props;
  const [option, setOption] = useState<ISelectOption | null>(null);
  const [options, setOptions] = useState<ISelectOption[]>([]);

  const onChangeSelect = useCallback(
    (selectedValue: string) => {
      const selectedOption = options.find((opt) => opt.value === selectedValue);
      setOption(selectedOption || null);

      const isSelectedAll = selectedOption?.value === defaultOption.value;
      const optionValue = selectedOption?.value ?? '';

      onChange(isSelectedAll ? '' : optionValue);
    },
    [onChange, options]
  );

  const getData = useCallback(async () => {
    try {
      setOptions([]);
      const list = (await getSpecializations()) ?? [];

      const options = list.map((item) => ({
        label: item ?? '',
        value: item ?? '',
      }));

      setOptions([defaultOption, ...options]);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await getData();
    })();
  }, [getData]);

  useEffect(() => {
    setOption(value ? { label: value, value } : null);
  }, [value]);

  return (
    <div className={cn('space-y-2', className)}>
      <Label>Выберите специализацию</Label>
      <Select value={option?.value || ''} onValueChange={onChangeSelect}>
        <SelectTrigger>
          <SelectValue placeholder="Выберите" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default memo(SpecializationSelect);
